import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ROUTES } from "../constants/routes";
import "../InteractiveSyllabus.css";
import footerIcon from "../assets/images/fotterIcon.svg";
import { AuthContext } from "../Hook/AuthProvider";
import {
  fetchSubjectProgress,
  fetchTopicProgress,
  fetchSubtopicCompletion,
  markSubtopicComplete,
  fetchSyllabusAggregate,
  getPriorityLearning,
  getProgressCard,
} from "../services/getSyllabus";
import Skeleton from "react-loading-skeleton";
import Studyplanner from "./StudyPlanner";
import { BannerContainer } from "../BannerComponent/Banner1";
import { List, LayoutGrid } from "lucide-react";
import SyllabusHeatmap from "./SyllabusHeatmap";

function InteractiveSyllabus({ Check, isSidebarColl }) {
  const { profileData } = useContext(AuthContext);

  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("list");
  const [activeSubject, setActiveSubject] = useState(
    JSON.parse(localStorage.getItem("subjectname")) || null,
  );
  const [priorityLearning, setPriorityLearning] = useState([]);
  const [activeSubtopic, setActiveSubtopic] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [syllabusData, setSyllabusData] = useState([]);
  const [topicData, setTopicData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rowloader, setRowloader] = useState(false);
  const [rowid, setRowid] = useState(null);
  const [error, setError] = useState(null);
  const [loadingTopics, setLoadingTopics] = useState({});
  const [loadingSubtopics, setLoadingSubtopics] = useState({});
  const [activeSyllabusTab, setActiveSyllabusTab] = useState("All");
  const [filteredSyllabusData, setFilteredSyllabusData] = useState([]);
  const [interactiveSyllabus, setInteractiveSyllabus] = useState(false);
  const [completionLoaded, setCompletionLoaded] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const userId = localStorage.getItem("user_id");
  const scrollRef = useRef(null);
  const timeoutRef = useRef(null);
  const USE_LOCAL_STORAGE = false; // change to true to persist across browser restarts
  const STORAGE_KEY = USE_LOCAL_STORAGE
    ? "ScrollBraker_scrollPos_local"
    : "ScrollBraker_scrollPos_session";

  useEffect(() => {
    const storage = USE_LOCAL_STORAGE
      ? window.localStorage
      : window.sessionStorage;

    // compute a key per route so different routes keep different positions
    const computeScrollKey = () =>
      `currentAffairs_scroll_${window.location.pathname}`;

    // helper to restore and attach listeners to the correct scroll element
    const attachToScrollContainer = () => {
      const el = scrollRef.current || window;

      const getPos = () =>
        el === window
          ? window.scrollY || window.pageYOffset || 0
          : el.scrollTop || 0;
      const setPos = (pos) => {
        if (el === window) window.scrollTo(0, pos);
        else el.scrollTop = pos;
      };

      // restore saved position (route-specific)
      try {
        const key = computeScrollKey();
        const saved = storage.getItem(key);
        if (saved) {
          const y = parseInt(saved, 10);
          if (!Number.isNaN(y)) setPos(y);
        }
      } catch (e) {
        // ignore storage errors
      }

      const handleScroll = () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          try {
            const key = computeScrollKey();
            storage.setItem(key, String(getPos()));
          } catch (e) {}
        }, 150);
      };

      if (el === window) {
        window.addEventListener("scroll", handleScroll, { passive: true });
      } else {
        el.addEventListener("scroll", handleScroll, { passive: true });
      }

      return () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        if (el === window) window.removeEventListener("scroll", handleScroll);
        else el.removeEventListener("scroll", handleScroll);
      };
    };

    // If the ref is not ready yet, poll (via rAF) until it's available, then attach.
    if (scrollRef.current) {
      const cleanup = attachToScrollContainer();
      return cleanup;
    }

    let rafId = null;
    let cleaned = false;
    const waitForRef = () => {
      if (scrollRef.current) {
        const cleanup = attachToScrollContainer();
        cleaned = true;
        return;
      }
      rafId = requestAnimationFrame(waitForRef);
    };
    waitForRef();

    return () => {
      if (!cleaned && rafId) cancelAnimationFrame(rafId);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  // Re-apply saved scroll position after content finishes loading (helps when data is fetched after mount)
  useEffect(() => {
    const storage = USE_LOCAL_STORAGE
      ? window.localStorage
      : window.sessionStorage;
    const computeScrollKey = () =>
      `interactivesyllabus_scroll_${window.location.pathname}`;

    // Only try to restore after loaders are done

    const applySaved = () => {
      try {
        const key = computeScrollKey();
        const saved = storage.getItem(key);
        if (saved) {
          const y = parseInt(saved, 10);
          if (!Number.isNaN(y)) {
            requestAnimationFrame(() => {
              const el = scrollRef.current || window;
              if (el === window) window.scrollTo(0, y);
              else el.scrollTop = y;
            });
          }
        }
      } catch (e) {}
    };

    // apply when data length changes or when loading finishes
    applySaved();
  }, [filteredSyllabusData.length]);

  useEffect(() => {
    const storedExpanded = localStorage.getItem("expandedkey");
    if (storedExpanded) {
      try {
        const parsedExpanded = JSON.parse(storedExpanded);
        setExpanded(parsedExpanded);
      } catch (error) {
        console.error("Error parsing expandedkey:", error);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("expandedkey", JSON.stringify(expanded));
  }, [expanded]);

  // Search functionality
  useEffect(() => {
    if (!searchQuery.trim()) {
      // If no search query, show all data based on active tab
      if (activeSyllabusTab === "All") {
        setFilteredSyllabusData(syllabusData);
      } else {
        setFilteredSyllabusData(
          syllabusData.filter(
            (subject) => subject.sub_type === activeSyllabusTab,
          ),
        );
      }
      return;
    }

    const query = searchQuery.toLowerCase().trim();
    const filteredSubjects = [];
    const subjectsToExpand = {};

    syllabusData.forEach((subject) => {
      // Check if subject matches tab filter
      if (
        activeSyllabusTab !== "All" &&
        subject.sub_type !== activeSyllabusTab
      ) {
        return;
      }

      let subjectHasMatch = false;
      const filteredTopics = [];

      if (subject.topics) {
        subject.topics.forEach((topic) => {
          let topicHasMatch = false;
          const filteredSubtopics = [];

          if (topic.subtopics && topic.subtopics.length > 0) {
            topic.subtopics.forEach((subtopic) => {
              if (
                subtopic.subtopic_name &&
                subtopic.subtopic_name.toLowerCase().includes(query)
              ) {
                filteredSubtopics.push(subtopic);
                topicHasMatch = true;
                subjectHasMatch = true;
              }
            });
          }

          if (topicHasMatch) {
            // Add topic with filtered subtopics
            filteredTopics.push({
              ...topic,
              subtopics: filteredSubtopics,
            });
            // Expand this topic
            subjectsToExpand[`${subject.subject_name}-${topic.topic_name}`] =
              true;
          }
        });
      }

      if (subjectHasMatch) {
        // Add subject with filtered topics
        filteredSubjects.push({
          ...subject,
          topics: filteredTopics,
        });
        // Expand this subject
        subjectsToExpand[subject.subject_name] = true;
      }
    });

    setFilteredSyllabusData(filteredSubjects);
    setExpanded((prev) => ({ ...prev, ...subjectsToExpand }));
  }, [searchQuery, syllabusData, activeSyllabusTab]);

  const syllabusTabs = [
    "All",
    "Core",
    "Strategic",
    // "Optionals",
    // "GS1",
    // "GS2",
    // "GS3",
    // "GS4",
    // "Prelims",
    // "Mains"
  ];

  // Fetch subjects data on component mount

  useEffect(() => {
    if (!activeSubject) return; // prevent running on empty subject

    const fetchData = async () => {
      try {
        const data = await getPriorityLearning(
          activeSubject,
          localStorage.getItem("user_id"),
        ); // pass subjectName and userId
        setPriorityLearning(data);
      } catch (error) {
        console.error("Failed to fetch priority learning:", error);
      } finally {
      }
    };

    fetchData();
  }, [activeSubject]);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    const loadSyllabusData = async () => {
      const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD
      const cached = localStorage.getItem("syllabusData");

      // 1. Initial Load: If we have cached data, show it immediately to avoid the loader
      if (cached) {
        try {
          const subjectsWithTopics = JSON.parse(cached);
          setSyllabusData(subjectsWithTopics);

          // Apply current tab filter to cached data
          if (activeSyllabusTab === "All") {
            setFilteredSyllabusData(subjectsWithTopics);
          } else {
            setFilteredSyllabusData(
              subjectsWithTopics.filter(
                (s) => s.sub_type === activeSyllabusTab,
              ),
            );
          }
          setError(null);
          setLoading(false);
          // If the cache is current for today, we still proceed to call the API in the background
          // to ensure any backend changes are reflected.
        } catch (e) {
          console.error("Error parsing cached syllabus data", e);
          setLoading(true);
        }
      } else {
        // 2. No cache: Show loader (unless it's a row-level update)
        rowloader ? setLoading(false) : setLoading(true);
      }

      // 3. Always fetch from API to synchronize state and localStorage with latest response
      try {
        const aggregateData = await fetchSyllabusAggregate(
          userId,
          "upsc_mains",
        );
        setSyllabusData(aggregateData);

        // Update filtered data with the fresh API response, respecting the current tab
        if (activeSyllabusTab === "All") {
          setFilteredSyllabusData(aggregateData);
        } else {
          setFilteredSyllabusData(
            aggregateData.filter((s) => s.sub_type === activeSyllabusTab),
          );
        }

        setError(null);
        localStorage.setItem("syllabusData", JSON.stringify(aggregateData));
        localStorage.setItem("syllabusDataDate", today);
      } catch (err) {
        console.error("Failed to load syllabus data:", err);
        // Only show error if we have no cached data to display
        if (!cached) {
          setError("Failed to load syllabus data");
          setSyllabusData([]);
          setFilteredSyllabusData([]);
        }
      } finally {
        setLoading(false);
        setRowloader(false);
      }
    };

    loadSyllabusData();
  }, [reload]);

  // Remove topic/subtopic fetching logic from toggleExpand, as all data is preloaded
  const toggleExpand = (key) => {
    if (!key) {
      console.error("toggleExpand called with undefined key");
      return;
    }
    setExpanded((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Modified toggle function for topics to load subtopics on demand
  const toggleTopicExpand = (subjectId, topicId, subjectName, topicName) => {
    const key = `${subjectName}-${topicName}`;

    // Toggle expansion
    setExpanded((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handelcomplete = async (subjectId, topicId, subtopicId) => {
    setCompletionLoaded(subtopicId);
    try {
      const result = await markSubtopicComplete({
        userId,
        subjectId,
        topicId,
        subtopicId,
      });

      // Fetch updated topic and subtopics
      const updatedTopics = await fetchTopicProgress(subjectId, userId);
      const updatedTopic = updatedTopics.find((t) => t.topic_id === topicId);
      const updatedSubtopics = await fetchSubtopicCompletion(
        subtopicId,
        userId,
      );

      // Update state
      setSyllabusData((prevData) =>
        prevData.map((subject) => {
          if (subject.subject_id === subjectId) {
            return {
              ...subject,
              topics: subject.topics.map((topic) =>
                topic.topic_id === topicId
                  ? {
                      ...updatedTopic,
                      subtopics: updatedSubtopics,
                      subtopicsLoaded: true,
                    }
                  : topic,
              ),
            };
          }
          return subject;
        }),
      );

      setFilteredSyllabusData((prevData) =>
        prevData.map((subject) => {
          if (subject.subject_id === subjectId) {
            return {
              ...subject,
              topics: subject.topics.map((topic) =>
                topic.topic_id === topicId
                  ? {
                      ...updatedTopic,
                      subtopics: updatedSubtopics,
                      subtopicsLoaded: true,
                    }
                  : topic,
              ),
            };
          }
          return subject;
        }),
      );

      // Fetch updated subject progress
      const updatedSubjects = await fetchSubjectProgress(userId, "upsc_mains");
      const updatedSubject = updatedSubjects.find(
        (s) => s.subject_id === subjectId,
      );

      if (updatedSubject) {
        // Update state
        setSyllabusData((prevData) =>
          prevData.map((subject) =>
            subject.subject_id === subjectId
              ? { ...subject, progress: updatedSubject.progress }
              : subject,
          ),
        );
        setFilteredSyllabusData((prevData) =>
          prevData.map((subject) =>
            subject.subject_id === subjectId
              ? { ...subject, progress: updatedSubject.progress }
              : subject,
          ),
        );

        // Update localStorage
        const cached = localStorage.getItem("syllabusData");
        if (cached) {
          let syllabusArr = JSON.parse(cached);
          syllabusArr = syllabusArr.map((subject) =>
            subject.subject_id === subjectId
              ? { ...subject, progress: updatedSubject.progress }
              : subject,
          );
          localStorage.setItem("syllabusData", JSON.stringify(syllabusArr));
        }
      }

      setReload((prev) => prev + 1);
    } catch (error) {
    } finally {
      setCompletionLoaded(0);
    }
  };

  const handleSubtopicClick = (mainTopic, subTopic) => {
    const key = `${mainTopic}__${subTopic}`;
    setActiveSubtopic((prev) => (prev === key ? null : key));
  };

  // Function to highlight matching text
  const highlightText = (text, query) => {
    if (!query || !text) return text;

    const regex = new RegExp(`(${query})`, "gi");
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <span key={index} className="search-highlight">
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  const handleSyllabusTabClick = (tabName) => {
    setActiveSyllabusTab(tabName);

    if (tabName === "All") {
      setFilteredSyllabusData(syllabusData);
    } else {
      const filtered = syllabusData.filter(
        (subject) => subject.sub_type === tabName,
      );
      setFilteredSyllabusData(filtered);
    }
  };

  const handleViewToggle = (tab) => {
    // The search bar is only shown in list view. Clear any active search
    // when switching to heatmap so it isn't filtered invisibly.
    if (tab === "heatmap") {
      setSearchQuery("");
    }
    setActiveTab(tab);
  };
  // Calculate total topics from API data
  const calculateTotalTopics = () => {
    return filteredSyllabusData.reduce((total, subject) => {
      return total + (subject.topics ? subject.topics.length : 0);
    }, 0);
  };
  const calculateTotalSubTopics = () => {
    return filteredSyllabusData.reduce((total, subject) => {
      return total + (subject.total_subtopics ? subject.total_subtopics : 0);
    }, 0);
  };

  // Calculate estimated time to cover (placeholder calculation)
  const calculateEstimatedTime = () => {
    return filteredSyllabusData.reduce((total, subject) => {
      return total + (subject?.total_estd_hours ? subject.total_estd_hours : 0);
    }, 0);
    // const totalTopics = calculateTotalTopics();
    // const avgTimePerTopic = 40; // minutes per topic
    // const totalMinutes = totalTopics * avgTimePerTopic;
    // const hours = Math.floor(totalMinutes / 60);
    // const minutes = totalMinutes % 60;
    // return `${hours}h ${minutes}min to cover`;
  };
  const [trackloading, setTrackloading] = useState(false);
  const [progressData, setProgressData] = useState({
    topic_progress: "0/0",
    completed_hours: 0,
    remaining_hours: 0,
  });
  useEffect(() => {
    const requestedSubjects = location.state?.syllabusSubjects;
    if (!requestedSubjects || requestedSubjects.length === 0) return;
    if (!syllabusData || syllabusData.length === 0) return;

    const newExpanded = { ...expanded };
    requestedSubjects.forEach((rs) => {
      const subject = syllabusData.find(
        (s) => s.subject_name === rs.name,
      );
      if (!subject) return;
      newExpanded[subject.subject_name] = true;
      (rs.topics || []).forEach((topicName) => {
        const topic = (subject.topics || []).find(
          (t) => t.topic_name === topicName,
        );
        if (topic) {
          newExpanded[`${subject.subject_name}-${topic.topic_name}`] = true;
        }
      });
    });
    setExpanded(newExpanded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syllabusData]);

  useEffect(() => {
    setTrackloading(true);
    const fetchProgress = async () => {
      try {
        const result = await getProgressCard(userId);
        setProgressData(result);
      } catch (err) {
      } finally {
        setTrackloading(false);
      }
    };

    fetchProgress();
  }, [syllabusData]);

  const { topic_progress, completed_hours, remaining_hours } = progressData;
  const [completed, total] = topic_progress.split("/").map(Number);
  const percentage = total === 0 ? 0 : Math.round((completed / total) * 100);

  return (
    <div className="syllabus-container">
      {interactiveSyllabus && (
        <>
          <Studyplanner setInteractiveSyllabus={setInteractiveSyllabus} />
        </>
      )}
      <div className="syllabus-content">
        <div>
          <div className="left-banner-container">
            <img src="/assets/images/banner_intsylla.svg" alt="star" />
            {/* <BannerContainer progress={3} /> */}
          </div>
        </div>
        <div className="w-o-img-cont">
          <div className="syllabus-content-left">
            {/* banner */}

            {/* header */}
            <div className="left-section-header">
              <span className="track-header">
                <span>
                  <svg
                    width="14"
                    height="15"
                    viewBox="0 0 14 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12.6667 7.5625V11.2083C12.6667 12.5833 12.6667 13.2707 12.2395 13.6979C11.8124 14.125 11.1249 14.125 9.75 14.125H2.82292C1.81615 14.125 1 13.3089 1 12.3021V12.3021C1 11.2953 1.81615 10.4792 2.82292 10.4792H9.75C11.1249 10.4792 11.8124 10.4792 12.2395 10.052C12.6667 9.6249 12.6667 8.93743 12.6667 7.5625V3.91667C12.6667 2.54174 12.6667 1.85427 12.2395 1.42714C11.8124 1 11.1249 1 9.75 1H3.91667C2.54174 1 1.85427 1 1.42714 1.42714C1 1.85427 1 2.54174 1 3.91667V12.3021"
                      stroke="#7B869B"
                      strokeWidth="1.09375"
                    />
                    <path
                      d="M4.646 4.64648L9.021 4.64648"
                      stroke="#7B869B"
                      strokeWidth="1.09375"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>{" "}
                Integrated Syllabus for General Studies - Prelims and Mains
              </span>
              {/* <span className="Integrated-text">
                Integrated Syllabus for General Studies - Prelims and Mains
              </span> */}
              {/* 
            <div className="header-tab-buttons">
              <button
                className={`header-toggle-btn ${
                  activeTab === "list" ? "active" : ""
                }`}
                onClick={() => setActiveTab("list")}
              >
                List View
              </button>
              <button
                className={`header-toggle-btn ${
                  activeTab === "insights" ? "active" : ""
                }`}
                onClick={() => setActiveTab("insights")}
              >
                Insights
              </button>
            </div> */}
            </div>
            {/* table */}
            <div className="tab-syllabus-container">
              {loading == true ? (
                <div className="syllabus-header ">
                  {Array.from({ length: 7 }).map((_, a) => (
                    <button key={a} className="syllabus-tab loading"></button>
                  ))}
                </div>
              ) : (
                <div className="syllabus-header">
                  <div className="syllabus-type-toggle-group">
                    {syllabusTabs.map((tab) => (
                      <button
                        key={tab}
                        type="button"
                        className={`syllabus-type-tab ${
                          activeSyllabusTab === tab ? "active" : ""
                        }`}
                        onClick={() => handleSyllabusTabClick(tab)}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>

                  <div className="syllabus-view-toggle-group">
                    <button
                      type="button"
                      className={`syllabus-view-tab ${
                        activeTab === "list" ? "active" : ""
                      }`}
                      onClick={() => handleViewToggle("list")}
                      title="List View"
                    >
                      <List size={13} />
                      <span>List</span>
                    </button>
                    <button
                      type="button"
                      className={`syllabus-view-tab ${
                        activeTab === "heatmap" ? "active" : ""
                      }`}
                      onClick={() => handleViewToggle("heatmap")}
                      title="Heatmap View"
                    >
                      <LayoutGrid size={13} />
                      <span>Heatmap</span>
                    </button>
                  </div>

                  {activeTab !== "heatmap" && (
                    <label className="isyllabus-search">
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <circle cx="11" cy="11" r="7" />
                        <path d="M20 20l-3.5-3.5" />
                      </svg>
                      <input
                        type="search"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search Subject/Topic"
                        aria-label="Search subjects and topics"
                      />
                    </label>
                  )}
                </div>
              )}

              {activeTab === "heatmap" ? (
                <SyllabusHeatmap
                  activeSyllabusTab={activeSyllabusTab}
                  syllabusData={syllabusData}
                  searchQuery={searchQuery}
                />
              ) : (
                <>
                  <div className="syllabus-summary">
                <span>
                  <span>
                    <svg
                      width="11"
                      height="10"
                      viewBox="0 0 11 10"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9.18206 0.5H8.54272C7.3926 0.5 6.31005 0.872094 5.41168 1.5791L5.19721 1.74688L4.98275 1.5791C4.08564 0.872787 3.00312 0.5 1.85171 0.5H1.21237C0.541913 0.5 0 1.04191 0 1.71237V7.08145C0 7.75191 0.541913 8.29382 1.21237 8.29382H1.56756C2.77453 8.29382 3.85566 8.65036 4.6553 9.30865C4.97734 9.57182 5.43671 9.56167 5.7513 9.2897C6.33854 8.78567 7.18557 8.29383 8.25589 8.29383H9.02987C9.8194 8.29383 10.3931 7.78439 10.3931 7.08145V1.71238C10.3931 1.04192 9.8512 0.500003 9.18074 0.500003L9.18206 0.5ZM4.85216 8.54011L4.33595 8.24919C3.58227 7.825 2.72373 7.60173 1.85439 7.60173H1.21506C0.92956 7.60173 0.695469 7.36764 0.695469 7.08214V1.71307C0.695469 1.42757 0.92956 1.19348 1.21506 1.19348H1.85439C2.91454 1.19348 3.93811 1.5764 4.73447 2.27393L4.85219 2.37811V8.54216L4.85216 8.54011ZM9.70165 7.08147C9.70165 7.36696 9.46755 7.60106 9.18206 7.60106H8.54272C7.67336 7.60106 6.81414 7.82432 6.06117 8.24851L5.54496 8.53943V2.37538L5.66268 2.2712C6.45966 1.57503 7.48331 1.19075 8.54276 1.19075H9.18209C9.46759 1.19075 9.70168 1.42484 9.70168 1.71034L9.70165 7.08147Z"
                        fill="#37A7D5"
                      />
                    </svg>
                  </span>{" "}
                  {calculateTotalTopics()} Topics
                </span>
                <span>
                  <span>
                    <svg
                      width="11"
                      height="10"
                      viewBox="0 0 11 10"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9.18206 0.5H8.54272C7.3926 0.5 6.31005 0.872094 5.41168 1.5791L5.19721 1.74688L4.98275 1.5791C4.08564 0.872787 3.00312 0.5 1.85171 0.5H1.21237C0.541913 0.5 0 1.04191 0 1.71237V7.08145C0 7.75191 0.541913 8.29382 1.21237 8.29382H1.56756C2.77453 8.29382 3.85566 8.65036 4.6553 9.30865C4.97734 9.57182 5.43671 9.56167 5.7513 9.2897C6.33854 8.78567 7.18557 8.29383 8.25589 8.29383H9.02987C9.8194 8.29383 10.3931 7.78439 10.3931 7.08145V1.71238C10.3931 1.04192 9.8512 0.500003 9.18074 0.500003L9.18206 0.5ZM4.85216 8.54011L4.33595 8.24919C3.58227 7.825 2.72373 7.60173 1.85439 7.60173H1.21506C0.92956 7.60173 0.695469 7.36764 0.695469 7.08214V1.71307C0.695469 1.42757 0.92956 1.19348 1.21506 1.19348H1.85439C2.91454 1.19348 3.93811 1.5764 4.73447 2.27393L4.85219 2.37811V8.54216L4.85216 8.54011ZM9.70165 7.08147C9.70165 7.36696 9.46755 7.60106 9.18206 7.60106H8.54272C7.67336 7.60106 6.81414 7.82432 6.06117 8.24851L5.54496 8.53943V2.37538L5.66268 2.2712C6.45966 1.57503 7.48331 1.19075 8.54276 1.19075H9.18209C9.46759 1.19075 9.70168 1.42484 9.70168 1.71034L9.70165 7.08147Z"
                        fill="#37A7D5"
                      />
                    </svg>
                  </span>{" "}
                  {calculateTotalSubTopics()} Subtopics
                </span>
                <span>
                  <span>
                    <svg
                      width="10"
                      height="11"
                      viewBox="0 0 10 11"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M5 10.5C7.75824 10.5 10 8.25824 10 5.5C10 2.74176 7.75824 0.5 5 0.5C2.24176 0.5 0 2.74176 0 5.5C0 8.25824 2.24176 10.5 5 10.5ZM5 1.40659C7.15385 1.40659 9.09341 3.34615 9.09341 5.5C9.09341 7.65385 7.15385 9.59341 5 9.59341C2.84615 9.59341 0.906593 7.65385 0.906593 5.5C0.906593 3.34615 2.84615 1.40659 5 1.40659Z"
                        fill="#37A7D5"
                      />
                      <path
                        d="M4.78041 6.37987H6.64854C6.95623 6.37987 7.21173 6.19306 7.21173 5.88537C7.21173 5.57767 6.95623 5.47328 6.64854 5.47328H5.13755V2.97328C5.13755 2.66559 5.01942 2.42383 4.71173 2.42383C4.40403 2.42383 4.23096 2.66559 4.23096 2.97328V5.83042C4.23096 6.13811 4.47272 6.37987 4.78041 6.37987Z"
                        fill="#37A7D5"
                      />
                    </svg>
                  </span>{" "}
                  {calculateEstimatedTime()} Hours to cover
                </span>
              </div>
              {loading == true ? (
                <div>
                  {Array.from({ length: 8 }).map((_, index) => (
                    <Skeleton
                      key={index}
                      height={40}
                      style={{ marginBottom: "15px" }}
                    />
                  ))}
                </div>
              ) : (
                <div className="subjects-div" ref={scrollRef}>
                  {filteredSyllabusData.map((subject, i) => (
                    <div key={i} className="syllabus-subject">
                      <div
                        className="syllabus-subject-header"
                        onClick={() => {
                          toggleExpand(subject.subject_name);
                          localStorage.setItem(
                            "subjectname",
                            JSON.stringify(subject.subject_name),
                          );
                          setActiveSubject(subject.subject_name);
                        }}
                      >
                        {/* <span>
                        {" "}
                        <svg
                          style={{ marginRight: "7px", marginTop: "-2px" }}
                          width="7"
                          height="6"
                          viewBox="0 0 7 6"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M3.79175 5.50625C3.55627 5.91412 2.96756 5.91412 2.73207 5.50625L0.0828791 0.917707C-0.152605 0.509837 0.141751 0 0.612719 0H5.91111C6.38208 0 6.67643 0.509838 6.44095 0.917708L3.79175 5.50625Z"
                            fill="#A2A8B4"
                          />
                        </svg>
                        {subject.subject_name}
                      </span> */}

                        <span>
                          {subject.subject_name !== null ? (
                            expanded[`${subject.subject_name}`] ? (
                              <svg
                                style={{
                                  marginRight: "7px",
                                  marginTop: "-2px",
                                }}
                                width="7"
                                height="6"
                                viewBox="0 0 7 6"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M2.73217 0.30625C2.96766 -0.101621 3.55637 -0.10162 3.79185 0.306251L6.44105 4.89479C6.67653 5.30266 6.38218 5.8125 5.91121 5.8125L0.612815 5.8125C0.141847 5.8125 -0.152507 5.30266 0.0829768 4.89479L2.73217 0.30625Z"
                                  fill="#49546C"
                                />
                              </svg>
                            ) : (
                              <svg
                                style={{
                                  marginRight: "7px",
                                  marginTop: "-2px",
                                }}
                                width="7"
                                height="6"
                                viewBox="0 0 7 6"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M3.79175 5.50625C3.55627 5.91412 2.96756 5.91412 2.73207 5.50625L0.0828791 0.917707C-0.152605 0.509837 0.141751 0 0.612719 0H5.91111C6.38208 0 6.67643 0.509838 6.44095 0.917708L3.79175 5.50625Z"
                                  fill="#A2A8B4"
                                />
                              </svg>
                            )
                          ) : (
                            <svg
                              style={{
                                marginRight: "7px",
                                marginTop: "-2px",
                              }}
                              width="7"
                              height="6"
                              viewBox="0 0 7 6"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M3.79175 5.50625C3.55627 5.91412 2.96756 5.91412 2.73207 5.50625L0.0828791 0.917707C-0.152605 0.509837 0.141751 0 0.612719 0H5.91111C6.38208 0 6.67643 0.509838 6.44095 0.917708L3.79175 5.50625Z"
                                fill="#A2A8B4"
                              />
                            </svg>
                          )}
                          {subject.subject_name}
                        </span>

                        <div className="progress-container-interactive">
                          <div className="number-of-topics">
                            {subject.total_topics} Topics /{" "}
                            {subject.total_subtopics} Sub Topics{" "}
                            <span className="number-of-topics">
                              <span className="round"></span>
                              {subject.total_estd_hours} Hours
                            </span>
                          </div>
                          <div className="syllabus-progress-bar">
                            {(() => {
                              const progressString =
                                typeof subject.progress === "string"
                                  ? subject.progress
                                  : "0/0";
                              const [completed, total] = progressString
                                .split("/")
                                .map(Number);
                              const percentage =
                                total === 0 ? 0 : (completed / total) * 100;
                              const fillStyle = {
                                width: `${percentage}%`,
                                backgroundColor:
                                  percentage > 50
                                    ? "#41dc8e"
                                    : percentage === 0
                                      ? "#41dc8e"
                                      : "#41dc8e",
                              };

                              return (
                                <div
                                  className="syllabus-progress-fill"
                                  style={fillStyle}
                                ></div>
                              );
                            })()}
                          </div>
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M8.00016 8.66732C8.36835 8.66732 8.66683 8.36884 8.66683 8.00065C8.66683 7.63246 8.36835 7.33398 8.00016 7.33398C7.63197 7.33398 7.3335 7.63246 7.3335 8.00065C7.3335 8.36884 7.63197 8.66732 8.00016 8.66732Z"
                              stroke="black"
                              strokeWidth="1.33333"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M8.00016 3.99935C8.36835 3.99935 8.66683 3.70087 8.66683 3.33268C8.66683 2.96449 8.36835 2.66602 8.00016 2.66602C7.63197 2.66602 7.3335 2.96449 7.3335 3.33268C7.3335 3.70087 7.63197 3.99935 8.00016 3.99935Z"
                              stroke="black"
                              strokeWidth="1.33333"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M8.00016 13.3333C8.36835 13.3333 8.66683 13.0349 8.66683 12.6667C8.66683 12.2985 8.36835 12 8.00016 12C7.63197 12 7.3335 12.2985 7.3335 12.6667C7.3335 13.0349 7.63197 13.3333 8.00016 13.3333Z"
                              stroke="black"
                              strokeWidth="1.33333"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                      </div>

                      {expanded[subject.subject_name] && (
                        <>
                          <div
                            className="syllabus-topic-list"
                            style={{ position: "relative" }}
                          >
                            {/* {profileData.user_type!="pro"  &&( <div className="button-premium" onClick={()=>{navigate("/Price")}}><span>Upgrade to Pro<img src="/assets/images/lightening.svg" alt=""  style={{marginLeft:"10px"}}/></span></div>)} */}
                            {subject.topics && subject.topics.length > 0 ? (
                              subject.topics.map(
                                (topic, idx) =>
                                  idx == 0 && (
                                    <div
                                      key={idx}
                                      className={`syllabus-topic-block ${idx > 0 && profileData?.user_type != "pro" && !searchQuery.trim() ? "extra-class" : ""}`}
                                    >
                                      <div
                                        className={`syllabus-topic-header ${topic.topicpriority}`}
                                        onClick={
                                          profileData?.user_type === "pro" ||
                                          idx === 0
                                            ? () =>
                                                toggleTopicExpand(
                                                  subject.subject_id,
                                                  topic.topic_id,
                                                  subject.subject_name,
                                                  topic.topic_name,
                                                )
                                            : undefined
                                        }
                                      >
                                        <span className="topicname">
                                          {topic.subtopics !== null ? (
                                            expanded[
                                              `${subject.subject_name}-${topic.topic_name}`
                                            ] ? (
                                              <svg
                                                style={{
                                                  marginRight: "7px",
                                                  marginTop: "-2px",
                                                }}
                                                width="7"
                                                height="6"
                                                viewBox="0 0 7 6"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                              >
                                                <path
                                                  d="M2.73217 0.30625C2.96766 -0.101621 3.55637 -0.10162 3.79185 0.306251L6.44105 4.89479C6.67653 5.30266 6.38218 5.8125 5.91121 5.8125L0.612815 5.8125C0.141847 5.8125 -0.152507 5.30266 0.0829768 4.89479L2.73217 0.30625Z"
                                                  fill="#49546C"
                                                />
                                              </svg>
                                            ) : (
                                              <svg
                                                style={{
                                                  marginRight: "7px",
                                                  marginTop: "-2px",
                                                }}
                                                width="7"
                                                height="6"
                                                viewBox="0 0 7 6"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                              >
                                                <path
                                                  d="M3.79175 5.50625C3.55627 5.91412 2.96756 5.91412 2.73207 5.50625L0.0828791 0.917707C-0.152605 0.509837 0.141751 0 0.612719 0H5.91111C6.38208 0 6.67643 0.509838 6.44095 0.917708L3.79175 5.50625Z"
                                                  fill="#A2A8B4"
                                                />
                                              </svg>
                                            )
                                          ) : (
                                            <svg
                                              style={{
                                                marginRight: "7px",
                                                marginTop: "-2px",
                                              }}
                                              width="7"
                                              height="6"
                                              viewBox="0 0 7 6"
                                              fill="none"
                                              xmlns="http://www.w3.org/2000/svg"
                                            >
                                              <path
                                                d="M3.79175 5.50625C3.55627 5.91412 2.96756 5.91412 2.73207 5.50625L0.0828791 0.917707C-0.152605 0.509837 0.141751 0 0.612719 0H5.91111C6.38208 0 6.67643 0.509838 6.44095 0.917708L3.79175 5.50625Z"
                                                fill="#A2A8B4"
                                              />
                                            </svg>
                                          )}{" "}
                                          {topic.topic_name}
                                          {topic.subtopics === null && (
                                            <span
                                              style={{
                                                fontSize: "12px",
                                                color: "#7B869B",
                                                marginLeft: "5px",
                                              }}
                                            >
                                              (Click to load)
                                            </span>
                                          )}
                                          <span
                                            className={`topicpriority-stamp ${topic.topicpriority?.toLowerCase()}`}
                                          >
                                            {topic.topicpriority}
                                          </span>
                                        </span>
                                        <span className="syllabus-topic-right">
                                          {topic.subtopic_progress !==
                                            undefined && (
                                            <span>
                                              <span className="syllabus-topic-count">
                                                {topic.subtopic_progress} Sub
                                                Topics
                                                <span>
                                                  <span class="round"></span>
                                                  {
                                                    topic.hours_progress.split(
                                                      "/",
                                                    )[1]
                                                  }{" "}
                                                  Hours
                                                </span>
                                              </span>
                                            </span>
                                          )}
                                        </span>
                                      </div>

                                      {expanded[
                                        `${subject.subject_name}-${topic.topic_name}`
                                      ] && (
                                        <div className="syllabus-subtopics">
                                          {loadingSubtopics[
                                            `${subject.subject_name}-${topic.topic_name}`
                                          ] ? (
                                            <div
                                              style={{
                                                padding: "20px",
                                                textAlign: "center",
                                                color: "#7B869B",
                                              }}
                                            >
                                              Loading subtopics...
                                            </div>
                                          ) : topic.subtopics &&
                                            topic.subtopics.length > 0 ? (
                                            topic.subtopics.map(
                                              (sub, subIdx) => {
                                                const isActive =
                                                  activeSubtopic ===
                                                  `${topic.topic_name}__${sub.title}`;
                                                return (
                                                  <div
                                                    key={subIdx}
                                                    className={`syllabus-subtopic ${
                                                      isActive ? "active" : ""
                                                    }`}
                                                    // onClick={() => handleSubtopicClick(topic.topic_name, sub.title)}
                                                  >
                                                    <div className="subtopic-div">
                                                      <span
                                                        style={{
                                                          fontSize: "13px",
                                                          color: "#49546C",
                                                        }}
                                                      >
                                                        {highlightText(
                                                          sub.subtopic_name,
                                                          searchQuery,
                                                        )}{" "}
                                                        <span>
                                                          {sub.gs && (
                                                            <span className="syllabus-gs-tag">
                                                              <svg
                                                                width="12"
                                                                height="12"
                                                                viewBox="0 0 12 12"
                                                                fill="none"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                              >
                                                                <path
                                                                  fillRule="evenodd"
                                                                  clipRule="evenodd"
                                                                  d="M1.52133 1C1.24163 1 1.01489 1.22674 1.01489 1.50645L1 5.4108C1 7.36872 2.5872 8.95592 4.54512 8.95592L6.6141 8.91126C6.84441 9.92618 7.75204 10.6838 8.83664 10.6838C10.0953 10.6838 11.1156 9.66349 11.1156 8.40482C11.1156 7.14615 10.0953 6.12581 8.83664 6.12581C7.75204 6.12581 6.84441 6.88345 6.6141 7.89837L4.54512 7.94303C3.14662 7.94303 2.01289 6.8093 2.01289 5.4108L2.02778 1.50645C2.02778 1.22674 1.80104 1 1.52133 1ZM7.57053 8.40482C7.57053 9.10407 8.13739 9.67093 8.83664 9.67093C9.53589 9.67093 10.1028 9.10407 10.1028 8.40482C10.1028 7.70557 9.53589 7.1387 8.83664 7.1387C8.13739 7.1387 7.57053 7.70557 7.57053 8.40482Z"
                                                                  fill="#A2A8B4"
                                                                />
                                                              </svg>
                                                              <span>
                                                                {sub.gs}
                                                              </span>
                                                            </span>
                                                          )}
                                                        </span>
                                                      </span>

                                                      {/* {sub.completed_flag && (
                                              <span className="syllabus-status complete">
                                                Complete
                                              </span>
                                            )} */}
                                                    </div>

                                                    <div className="syllabus-actions">
                                                      {sub.completed_flag ? (
                                                        <div className="complet-revist">
                                                          <span className="syllabus-status complete active-complete complete">
                                                            Complete
                                                          </span>
                                                          <span
                                                            className="revisit-button"
                                                            onClick={() => {
                                                              const message = `Short Notes on ${highlightText(sub.subtopic_name, searchQuery)} from ${topic.topic_name}, ${subject.subject_name}`;
                                                              navigate(
                                                                ROUTES.CHAT,
                                                                {
                                                                  state: {
                                                                    toipic:
                                                                      "Learn Topic",
                                                                    initialMessages:
                                                                      [
                                                                        {
                                                                          sender:
                                                                            "user",
                                                                          message,
                                                                          prompt:
                                                                            "Quick Topic Summary",
                                                                        },
                                                                      ],
                                                                  },
                                                                },
                                                              );
                                                            }}
                                                          >
                                                            Revisit This
                                                          </span>
                                                        </div>
                                                      ) : (
                                                        <>
                                                          {completionLoaded ===
                                                          sub.subtopic_id ? (
                                                            <div class="loader-class-buttonclick-interactive-syllabus"></div>
                                                          ) : (
                                                            <div className="button-div">
                                                              <button
                                                                className="syllabus-learn-btn-mark-as-complete"
                                                                onClick={() => {
                                                                  setRowloader(
                                                                    true,
                                                                  );
                                                                  setRowid(
                                                                    sub.subtopic_id,
                                                                  );
                                                                  handelcomplete(
                                                                    subject.subject_id,
                                                                    topic.topic_id,
                                                                    sub.subtopic_id,
                                                                  );
                                                                }}
                                                              >
                                                                Mark as complete
                                                              </button>
                                                              <button
                                                                className="syllabus-learn-btn"
                                                                onClick={() => {
                                                                  const message = `Short Notes on ${highlightText(sub.subtopic_name, searchQuery)} from ${topic.topic_name}, ${subject.subject_name}`;
                                                                  navigate(
                                                                    ROUTES.CHAT,
                                                                    {
                                                                      state: {
                                                                        toipic:
                                                                          "Learn Topic",
                                                                        initialMessages:
                                                                          [
                                                                            {
                                                                              sender:
                                                                                "user",
                                                                              message,
                                                                              prompt:
                                                                                "Quick Topic Summary",
                                                                            },
                                                                          ],
                                                                      },
                                                                    },
                                                                  );
                                                                }}
                                                              >
                                                                Learn This
                                                              </button>
                                                            </div>
                                                          )}
                                                        </>
                                                      )}
                                                    </div>
                                                  </div>
                                                );
                                              },
                                            )
                                          ) : (
                                            <div
                                              style={{
                                                padding: "20px",
                                                textAlign: "center",
                                                color: "#7B869B",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                              }}
                                            >
                                              <div class="loader-class"></div>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  ),
                              )
                            ) : (
                              <div
                                style={{
                                  padding: "20px",
                                  textAlign: "center",
                                  color: "#7B869B",
                                }}
                              >
                                No topics available
                              </div>
                            )}
                          </div>
                          <div
                            className="syllabus-topic-list"
                            style={{ position: "relative" }}
                          >
                            {profileData?.user_type != "pro" &&
                              !searchQuery.trim() && (
                                <div
                                  className="button-premium"
                                  onClick={() => {
                                    navigate("/Price");
                                  }}
                                >
                                  <span>
                                    Upgrade to Pro
                                    <img
                                      src="/assets/images/lightening.svg"
                                      alt=""
                                      style={{ marginLeft: "10px" }}
                                    />
                                  </span>
                                </div>
                              )}
                            {subject.topics && subject.topics.length > 0 ? (
                              subject.topics.map(
                                (topic, idx) =>
                                  idx != 0 && (
                                    <div
                                      key={idx}
                                      className={`syllabus-topic-block ${idx > 0 && profileData?.user_type != "pro" && !searchQuery.trim() ? "extra-class" : ""}`}
                                    >
                                      <div
                                        className={`syllabus-topic-header ${topic.topicpriority}`}
                                        onClick={
                                          profileData?.user_type === "pro" ||
                                          idx === 0
                                            ? () =>
                                                toggleTopicExpand(
                                                  subject.subject_id,
                                                  topic.topic_id,
                                                  subject.subject_name,
                                                  topic.topic_name,
                                                )
                                            : undefined
                                        }
                                      >
                                        <span className="topicname">
                                          {topic.subtopics !== null ? (
                                            expanded[
                                              `${subject.subject_name}-${topic.topic_name}`
                                            ] ? (
                                              <svg
                                                style={{
                                                  marginRight: "7px",
                                                  marginTop: "-2px",
                                                }}
                                                width="7"
                                                height="6"
                                                viewBox="0 0 7 6"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                              >
                                                <path
                                                  d="M2.73217 0.30625C2.96766 -0.101621 3.55637 -0.10162 3.79185 0.306251L6.44105 4.89479C6.67653 5.30266 6.38218 5.8125 5.91121 5.8125L0.612815 5.8125C0.141847 5.8125 -0.152507 5.30266 0.0829768 4.89479L2.73217 0.30625Z"
                                                  fill="#49546C"
                                                />
                                              </svg>
                                            ) : (
                                              <svg
                                                style={{
                                                  marginRight: "7px",
                                                  marginTop: "-2px",
                                                }}
                                                width="7"
                                                height="6"
                                                viewBox="0 0 7 6"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                              >
                                                <path
                                                  d="M3.79175 5.50625C3.55627 5.91412 2.96756 5.91412 2.73207 5.50625L0.0828791 0.917707C-0.152605 0.509837 0.141751 0 0.612719 0H5.91111C6.38208 0 6.67643 0.509838 6.44095 0.917708L3.79175 5.50625Z"
                                                  fill="#A2A8B4"
                                                />
                                              </svg>
                                            )
                                          ) : (
                                            <svg
                                              style={{
                                                marginRight: "7px",
                                                marginTop: "-2px",
                                              }}
                                              width="7"
                                              height="6"
                                              viewBox="0 0 7 6"
                                              fill="none"
                                              xmlns="http://www.w3.org/2000/svg"
                                            >
                                              <path
                                                d="M3.79175 5.50625C3.55627 5.91412 2.96756 5.91412 2.73207 5.50625L0.0828791 0.917707C-0.152605 0.509837 0.141751 0 0.612719 0H5.91111C6.38208 0 6.67643 0.509838 6.44095 0.917708L3.79175 5.50625Z"
                                                fill="#A2A8B4"
                                              />
                                            </svg>
                                          )}{" "}
                                          {topic.topic_name}
                                          {topic.subtopics === null && (
                                            <span
                                              style={{
                                                fontSize: "12px",
                                                color: "#7B869B",
                                                marginLeft: "5px",
                                              }}
                                            >
                                              (Click to load)
                                            </span>
                                          )}
                                          <span
                                            className={`topicpriority-stamp ${topic.topicpriority?.toLowerCase()}`}
                                          >
                                            {topic.topicpriority}
                                          </span>
                                        </span>
                                        <span className="syllabus-topic-right">
                                          {topic.subtopic_progress !==
                                            undefined && (
                                            <span className="syllabus-topic-count">
                                              {topic.subtopic_progress} Sub
                                              Topics
                                              <span>
                                                <span class="round"></span>
                                                {
                                                  topic.hours_progress.split(
                                                    "/",
                                                  )[1]
                                                }{" "}
                                                Hours
                                              </span>
                                            </span>
                                          )}
                                        </span>
                                      </div>

                                      {expanded[
                                        `${subject.subject_name}-${topic.topic_name}`
                                      ] && (
                                        <div className="syllabus-subtopics">
                                          {loadingSubtopics[
                                            `${subject.subject_name}-${topic.topic_name}`
                                          ] ? (
                                            <div
                                              style={{
                                                padding: "20px",
                                                textAlign: "center",
                                                color: "#7B869B",
                                              }}
                                            >
                                              Loading subtopics...
                                            </div>
                                          ) : topic.subtopics &&
                                            topic.subtopics.length > 0 ? (
                                            topic.subtopics.map(
                                              (sub, subIdx) => {
                                                const isActive =
                                                  activeSubtopic ===
                                                  `${topic.topic_name}__${sub.title}`;
                                                return (
                                                  <div
                                                    key={subIdx}
                                                    className={`syllabus-subtopic ${
                                                      isActive ? "active" : ""
                                                    }`}
                                                    // onClick={() => handleSubtopicClick(topic.topic_name, sub.title)}
                                                  >
                                                    <div className="subtopic-div">
                                                      <span
                                                        style={{
                                                          fontSize: "13px",
                                                          color: "#49546C",
                                                        }}
                                                      >
                                                        {highlightText(
                                                          sub.subtopic_name,
                                                          searchQuery,
                                                        )}{" "}
                                                        <span>
                                                          {sub.gs && (
                                                            <span className="syllabus-gs-tag">
                                                              <svg
                                                                width="12"
                                                                height="12"
                                                                viewBox="0 0 12 12"
                                                                fill="none"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                              >
                                                                <path
                                                                  fillRule="evenodd"
                                                                  clipRule="evenodd"
                                                                  d="M1.52133 1C1.24163 1 1.01489 1.22674 1.01489 1.50645L1 5.4108C1 7.36872 2.5872 8.95592 4.54512 8.95592L6.6141 8.91126C6.84441 9.92618 7.75204 10.6838 8.83664 10.6838C10.0953 10.6838 11.1156 9.66349 11.1156 8.40482C11.1156 7.14615 10.0953 6.12581 8.83664 6.12581C7.75204 6.12581 6.84441 6.88345 6.6141 7.89837L4.54512 7.94303C3.14662 7.94303 2.01289 6.8093 2.01289 5.4108L2.02778 1.50645C2.02778 1.22674 1.80104 1 1.52133 1ZM7.57053 8.40482C7.57053 9.10407 8.13739 9.67093 8.83664 9.67093C9.53589 9.67093 10.1028 9.10407 10.1028 8.40482C10.1028 7.70557 9.53589 7.1387 8.83664 7.1387C8.13739 7.1387 7.57053 7.70557 7.57053 8.40482Z"
                                                                  fill="#A2A8B4"
                                                                />
                                                              </svg>
                                                              <span>
                                                                {sub.gs}
                                                              </span>
                                                            </span>
                                                          )}
                                                        </span>
                                                      </span>

                                                      {/* {sub.completed_flag && (
                                              <span className="syllabus-status complete">
                                                Complete
                                              </span>
                                            )} */}
                                                    </div>

                                                    <div className="syllabus-actions">
                                                      {sub.completed_flag ? (
                                                        <div className="complet-revist">
                                                          <span className="syllabus-status complete active-complete complete">
                                                            Complete
                                                          </span>
                                                          <span
                                                            className="revisit-button"
                                                            onClick={() => {
                                                              const message = `Short Notes on ${highlightText(sub.subtopic_name, searchQuery)} from ${topic.topic_name}, ${subject.subject_name}`;
                                                              navigate(
                                                                ROUTES.CHAT,
                                                                {
                                                                  state: {
                                                                    toipic:
                                                                      "Learn Topic",
                                                                    initialMessages:
                                                                      [
                                                                        {
                                                                          sender:
                                                                            "user",
                                                                          message,
                                                                          prompt:
                                                                            "Quick Topic Summary",
                                                                        },
                                                                      ],
                                                                  },
                                                                },
                                                              );
                                                            }}
                                                          >
                                                            Revisit This
                                                          </span>
                                                        </div>
                                                      ) : (
                                                        <>
                                                          {completionLoaded ===
                                                          sub.subtopic_id ? (
                                                            <div class="loader-class-buttonclick-interactive-syllabus"></div>
                                                          ) : (
                                                            <div className="button-div">
                                                              <button
                                                                className="syllabus-learn-btn-mark-as-complete"
                                                                onClick={() => {
                                                                  setRowloader(
                                                                    true,
                                                                  );
                                                                  setRowid(
                                                                    sub.subtopic_id,
                                                                  );
                                                                  handelcomplete(
                                                                    subject.subject_id,
                                                                    topic.topic_id,
                                                                    sub.subtopic_id,
                                                                  );
                                                                }}
                                                              >
                                                                Mark as complete
                                                              </button>
                                                              <button
                                                                className="syllabus-learn-btn"
                                                                onClick={() => {
                                                                  const message = `Short Notes on ${highlightText(sub.subtopic_name, searchQuery)} from ${topic.topic_name}, ${subject.subject_name}`;
                                                                  navigate(
                                                                    ROUTES.CHAT,
                                                                    {
                                                                      state: {
                                                                        toipic:
                                                                          "Learn Topic",
                                                                        initialMessages:
                                                                          [
                                                                            {
                                                                              sender:
                                                                                "user",
                                                                              message,
                                                                              prompt:
                                                                                "Quick Topic Summary",
                                                                            },
                                                                          ],
                                                                      },
                                                                    },
                                                                  );
                                                                }}
                                                              >
                                                                Learn This
                                                              </button>
                                                            </div>
                                                          )}
                                                        </>
                                                      )}
                                                    </div>
                                                  </div>
                                                );
                                              },
                                            )
                                          ) : (
                                            <div
                                              style={{
                                                padding: "20px",
                                                textAlign: "center",
                                                color: "#7B869B",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                              }}
                                            >
                                              <div class="loader-class"></div>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  ),
                              )
                            ) : (
                              <div
                                style={{
                                  padding: "20px",
                                  textAlign: "center",
                                  color: "#7B869B",
                                }}
                              >
                                No topics available
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="syllabus-footer">
                <span>
                  <img
                    src={footerIcon}
                    alt=""
                    style={{ marginRight: "12px" }}
                  />
                  Every topper starts from zero! Choose a topic and take your
                  first step today.
                </span>
              </div>
                </>
              )}
            </div>
          </div>
          <div className="syllabus-content-right">
            {/* banner */}
            {/* <div className="right-banner-container">
            <button className="goal-button">
              <span>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10 10.6993V3L15.5995 5.79973L10 8.59946"
                    stroke="white"
                    stroke-width="1.2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M15.992 8.75733C16.3644 9.90532 16.3993 11.1361 16.0924 12.3034C15.7855 13.4706 15.1497 14.5251 14.2607 15.3414C13.3718 16.1577 12.267 16.7014 11.0779 16.9079C9.88881 17.1144 8.66542 16.975 7.55328 16.5062C6.44114 16.0374 5.48708 15.259 4.80459 14.2636C4.12211 13.2682 3.73994 12.0977 3.70355 10.8914C3.66716 9.68502 3.97808 8.49361 4.59932 7.45888C5.22056 6.42415 6.12596 5.58966 7.20782 5.05469"
                    stroke="white"
                    stroke-width="1.2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M7.20258 8.59766C6.85185 9.06453 6.62351 9.61174 6.53837 10.1894C6.45324 10.7671 6.51401 11.357 6.71515 11.9052C6.91629 12.4534 7.2514 12.9425 7.68996 13.3281C8.12852 13.7136 8.65658 13.9833 9.22604 14.1125C9.79549 14.2418 10.3882 14.2265 10.9503 14.068C11.5123 13.9096 12.0257 13.613 12.4438 13.2054C12.8619 12.7977 13.1713 12.2919 13.3439 11.7341C13.5165 11.1762 13.5468 10.5841 13.432 10.0115"
                    stroke="white"
                    stroke-width="1.2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>
              Set daily goal
            </button>
          </div> */}

            {/* Track */}
            {/* {trackloading == true ? (
              <Skeleton variant="rounded" width={280} height={180} />
            ) : (
              <div className="right-track-container">
                <p className="track-header">
                  <span>
                    <svg
                      width="19"
                      height="19"
                      viewBox="0 0 19 19"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M8.84366 5.55213C10.043 5.55213 11.1288 6.03825 11.915 6.82446C12.7012 7.61016 13.1874 8.69647 13.1874 9.89531C13.1874 10.5423 13.0445 11.1579 12.7891 11.7118C12.5245 12.2858 12.138 12.792 11.6632 13.1979C11.4813 13.3536 11.3431 13.533 11.2516 13.7318C11.2316 13.775 11.2136 13.8197 11.1982 13.8649C11.1946 13.8788 11.19 13.8927 11.1848 13.906C11.1381 14.0566 11.1144 14.219 11.1144 14.3911V15.0633C11.1144 15.6876 10.8596 16.2554 10.4479 16.6665C10.0369 17.0776 9.46904 17.333 8.84469 17.333C8.22035 17.333 7.65252 17.0776 7.24143 16.6665C6.83034 16.2554 6.57495 15.6876 6.57495 15.0633V14.3911C6.57495 14.219 6.55131 14.0561 6.50404 13.9055C6.4989 13.8927 6.49479 13.8793 6.49119 13.8654C6.47526 13.8197 6.45779 13.775 6.43775 13.7318C6.34628 13.533 6.20805 13.3536 6.02563 13.1979C5.55082 12.7925 5.16439 12.2858 4.89975 11.7118C4.64436 11.1579 4.50202 10.5423 4.50202 9.89531C4.50202 8.69595 4.98814 7.61016 5.77435 6.82446C6.56056 6.03825 7.64636 5.55213 8.8452 5.55213H8.84366ZM0.843816 10.2709C0.626451 10.2709 0.450195 10.0947 0.450195 9.87733C0.450195 9.65996 0.626451 9.48422 0.843816 9.48422H2.18757C2.40494 9.48422 2.58119 9.66048 2.58119 9.87733C2.58119 10.0947 2.40494 10.2709 2.18757 10.2709H0.843816ZM2.90904 4.49922C2.75539 4.34557 2.75539 4.09635 2.90904 3.9427C3.06268 3.78906 3.31139 3.78906 3.46504 3.9427L4.41518 4.89284C4.56882 5.04649 4.56882 5.29571 4.41518 5.44936C4.26153 5.603 4.01282 5.603 3.85917 5.44936L2.90904 4.49922ZM16.8435 9.48422C17.0609 9.48422 17.2371 9.66048 17.2371 9.87733C17.2371 10.0947 17.0609 10.2709 16.8435 10.2709H15.4998C15.2824 10.2709 15.1061 10.0947 15.1061 9.87733C15.1061 9.65996 15.2824 9.48422 15.4998 9.48422H16.8435ZM14.2223 3.9427C14.3759 3.78906 14.6246 3.78906 14.7783 3.9427C14.9319 4.09635 14.9319 4.34506 14.7783 4.49922L13.8282 5.44936C13.6745 5.603 13.4253 5.603 13.2716 5.44936C13.118 5.29571 13.118 5.04649 13.2716 4.89284L14.2218 3.9427H14.2223ZM8.45004 1.87748C8.45004 1.66012 8.6263 1.48438 8.84366 1.48438C9.06103 1.48438 9.23728 1.66063 9.23728 1.87748V3.22124C9.23728 3.4386 9.06103 3.61486 8.84366 3.61486C8.6263 3.61486 8.45004 3.4386 8.45004 3.22124V1.87748ZM8.45004 13.3701V10.3491C8.45004 10.1317 8.6263 9.95595 8.84366 9.95595C9.06103 9.95595 9.23728 10.1322 9.23728 10.3491V13.3701H10.5538C10.6925 13.0818 10.893 12.8233 11.153 12.6018C11.542 12.2694 11.859 11.8537 12.0764 11.3835C12.2845 10.9323 12.4006 10.4282 12.4006 9.89531C12.4006 8.91332 12.0024 8.02382 11.359 7.38046C10.7157 6.7371 9.82617 6.33886 8.84418 6.33886C7.86218 6.33886 6.97319 6.73659 6.32932 7.38046C5.68596 8.02382 5.28772 8.91332 5.28772 9.89531C5.28772 10.4282 5.40385 10.9318 5.61197 11.3835C5.82882 11.8542 6.14587 12.2694 6.53487 12.6018C6.79488 12.8233 6.99529 13.0818 7.13403 13.3701H8.45004ZM10.3375 14.1568H8.84366H7.34986C7.35705 14.2339 7.36065 14.312 7.36065 14.3911V15.0633C7.36065 15.4707 7.52765 15.8418 7.79641 16.1105C8.06516 16.3793 8.43617 16.5463 8.84366 16.5463C9.25116 16.5463 9.62217 16.3793 9.89092 16.1105C10.1602 15.8418 10.3267 15.4707 10.3267 15.0633V14.3911C10.3267 14.312 10.3303 14.2339 10.3375 14.1568Z"
                        fill="#7B869B"
                        stroke="#7B869B"
                        stroke-width="0.5"
                      />
                    </svg>
                  </span>
                  Track Your Progress
                </p>

                <div className="track-card">
                  <div className="track-progress">
                    <div className="track-progress-bar">
                      <span className="track-progress-text">{percentage}%</span>
                      <div className="track-progress-outer">
                        <div
                          className="track-progress-inner"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                    <p className="track-message">
                      You are doing steady progress! Keep going.
                    </p>
                  </div>
                  <div className="track-info">
                    <div className="track-details">
                      <div className="track-row">
                        <span className="track-label">Completed Topics</span>
                        <span className="track-value">{topic_progress}</span>
                      </div>
                      <div className="track-row">
                        <span className="track-label">Completed Hours</span>
                        <span className="track-value">
                          {completed_hours} hrs
                        </span>
                      </div>
                      <div className="track-row">
                        <span className="track-label">Remaining Hours</span>
                        <span className="track-value">
                          {remaining_hours} hrs
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )} */}
            <div className="right-track-container">
              <p className="track-header">
                <span>
                  <svg
                    width="19"
                    height="19"
                    viewBox="0 0 19 19"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8.84366 5.55213C10.043 5.55213 11.1288 6.03825 11.915 6.82446C12.7012 7.61016 13.1874 8.69647 13.1874 9.89531C13.1874 10.5423 13.0445 11.1579 12.7891 11.7118C12.5245 12.2858 12.138 12.792 11.6632 13.1979C11.4813 13.3536 11.3431 13.533 11.2516 13.7318C11.2316 13.775 11.2136 13.8197 11.1982 13.8649C11.1946 13.8788 11.19 13.8927 11.1848 13.906C11.1381 14.0566 11.1144 14.219 11.1144 14.3911V15.0633C11.1144 15.6876 10.8596 16.2554 10.4479 16.6665C10.0369 17.0776 9.46904 17.333 8.84469 17.333C8.22035 17.333 7.65252 17.0776 7.24143 16.6665C6.83034 16.2554 6.57495 15.6876 6.57495 15.0633V14.3911C6.57495 14.219 6.55131 14.0561 6.50404 13.9055C6.4989 13.8927 6.49479 13.8793 6.49119 13.8654C6.47526 13.8197 6.45779 13.775 6.43775 13.7318C6.34628 13.533 6.20805 13.3536 6.02563 13.1979C5.55082 12.7925 5.16439 12.2858 4.89975 11.7118C4.64436 11.1579 4.50202 10.5423 4.50202 9.89531C4.50202 8.69595 4.98814 7.61016 5.77435 6.82446C6.56056 6.03825 7.64636 5.55213 8.8452 5.55213H8.84366ZM0.843816 10.2709C0.626451 10.2709 0.450195 10.0947 0.450195 9.87733C0.450195 9.65996 0.626451 9.48422 0.843816 9.48422H2.18757C2.40494 9.48422 2.58119 9.66048 2.58119 9.87733C2.58119 10.0947 2.40494 10.2709 2.18757 10.2709H0.843816ZM2.90904 4.49922C2.75539 4.34557 2.75539 4.09635 2.90904 3.9427C3.06268 3.78906 3.31139 3.78906 3.46504 3.9427L4.41518 4.89284C4.56882 5.04649 4.56882 5.29571 4.41518 5.44936C4.26153 5.603 4.01282 5.603 3.85917 5.44936L2.90904 4.49922ZM16.8435 9.48422C17.0609 9.48422 17.2371 9.66048 17.2371 9.87733C17.2371 10.0947 17.0609 10.2709 16.8435 10.2709H15.4998C15.2824 10.2709 15.1061 10.0947 15.1061 9.87733C15.1061 9.65996 15.2824 9.48422 15.4998 9.48422H16.8435ZM14.2223 3.9427C14.3759 3.78906 14.6246 3.78906 14.7783 3.9427C14.9319 4.09635 14.9319 4.34506 14.7783 4.49922L13.8282 5.44936C13.6745 5.603 13.4253 5.603 13.2716 5.44936C13.118 5.29571 13.118 5.04649 13.2716 4.89284L14.2218 3.9427H14.2223ZM8.45004 1.87748C8.45004 1.66012 8.6263 1.48438 8.84366 1.48438C9.06103 1.48438 9.23728 1.66063 9.23728 1.87748V3.22124C9.23728 3.4386 9.06103 3.61486 8.84366 3.61486C8.6263 3.61486 8.45004 3.4386 8.45004 3.22124V1.87748ZM8.45004 13.3701V10.3491C8.45004 10.1317 8.6263 9.95595 8.84366 9.95595C9.06103 9.95595 9.23728 10.1322 9.23728 10.3491V13.3701H10.5538C10.6925 13.0818 10.893 12.8233 11.153 12.6018C11.542 12.2694 11.859 11.8537 12.0764 11.3835C12.2845 10.9323 12.4006 10.4282 12.4006 9.89531C12.4006 8.91332 12.0024 8.02382 11.359 7.38046C10.7157 6.7371 9.82617 6.33886 8.84418 6.33886C7.86218 6.33886 6.97319 6.73659 6.32932 7.38046C5.68596 8.02382 5.28772 8.91332 5.28772 9.89531C5.28772 10.4282 5.40385 10.9318 5.61197 11.3835C5.82882 11.8542 6.14587 12.2694 6.53487 12.6018C6.79488 12.8233 6.99529 13.0818 7.13403 13.3701H8.45004ZM10.3375 14.1568H8.84366H7.34986C7.35705 14.2339 7.36065 14.312 7.36065 14.3911V15.0633C7.36065 15.4707 7.52765 15.8418 7.79641 16.1105C8.06516 16.3793 8.43617 16.5463 8.84366 16.5463C9.25116 16.5463 9.62217 16.3793 9.89092 16.1105C10.1602 15.8418 10.3267 15.4707 10.3267 15.0633V14.3911C10.3267 14.312 10.3303 14.2339 10.3375 14.1568Z"
                      fill="#7B869B"
                      stroke="#7B869B"
                      strokeWidth="0.5"
                    />
                  </svg>
                </span>
                Track Your Progress
              </p>

              {trackloading ? (
                <div className="">
                  <Skeleton variant="rounded" width="100%" height={185} />
                </div>
              ) : (
                <div className="track-card">
                  <div className="track-progress">
                    <div className="track-progress-bar">
                      <span className="track-progress-text">{percentage}%</span>
                      <div className="track-progress-outer">
                        <div
                          className="track-progress-inner"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                    <p className="track-message">
                      You are doing steady progress! Keep going.
                    </p>
                  </div>

                  <div className="track-info">
                    <div className="track-details">
                      <div className="track-row">
                        <span className="track-label">Completed Topics</span>
                        <span className="track-value">{topic_progress}</span>
                      </div>

                      <div className="track-row">
                        <span className="track-label">Completed Hours</span>
                        <span className="track-value">
                          {completed_hours} hrs
                        </span>
                      </div>

                      <div className="track-row">
                        <span className="track-label">Remaining Hours</span>
                        <span className="track-value">
                          {remaining_hours} hrs
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* priority */}
            <div className="right-priority-container">
              <p className="priority-header">
                <span>
                  <svg
                    width="18"
                    height="19"
                    viewBox="0 0 18 19"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9 1C7.41775 1 5.87103 1.46919 4.55544 2.34824C3.23985 3.22729 2.21447 4.47672 1.60897 5.93853C1.00347 7.40034 0.84504 9.00887 1.15372 10.5607C1.4624 12.1126 2.22433 13.538 3.34315 14.6569C4.46197 15.7757 5.88743 16.5376 7.43928 16.8463C8.99113 17.155 10.5997 16.9965 12.0615 16.391C13.5233 15.7855 14.7727 14.7602 15.6518 13.4446C16.5308 12.129 17 10.5822 17 9C16.9969 6.87921 16.1531 4.84617 14.6535 3.34654C13.1538 1.84692 11.1208 1.00308 9 1ZM9 16.1273C7.59036 16.1273 6.21238 15.7093 5.0403 14.9261C3.86823 14.143 2.95471 13.0298 2.41526 11.7275C1.87582 10.4251 1.73467 8.99209 2.00968 7.60954C2.28469 6.22698 2.96349 4.95702 3.96026 3.96026C4.95703 2.96349 6.22699 2.28468 7.60954 2.00968C8.9921 1.73467 10.4252 1.87581 11.7275 2.41526C13.0298 2.9547 14.143 3.86822 14.9261 5.0403C15.7093 6.21237 16.1273 7.59036 16.1273 9C16.1242 10.8893 15.3723 12.7004 14.0363 14.0363C12.7004 15.3723 10.8893 16.1242 9 16.1273Z"
                      fill="#7B869B"
                    />
                    <path
                      d="M13.0727 6.52729L10.8909 6.2131L9.91346 4.23492C9.82947 4.06433 9.69938 3.92068 9.53794 3.82022C9.3765 3.71976 9.19015 3.66651 9.00001 3.66651C8.80986 3.66651 8.62352 3.71976 8.46208 3.82022C8.30063 3.92068 8.17055 4.06433 8.08655 4.23492L7.1091 6.2131L4.92728 6.52729C4.73986 6.55547 4.56394 6.63511 4.4191 6.75735C4.27426 6.87959 4.16619 7.03963 4.10692 7.21965C4.04443 7.39901 4.03367 7.59234 4.07585 7.77753C4.11803 7.96272 4.21146 8.13231 4.34546 8.26692L5.92801 9.80292L5.55565 11.9789C5.52193 12.1657 5.54174 12.3583 5.61277 12.5343C5.68381 12.7104 5.80317 12.8627 5.9571 12.9738C6.11196 13.0848 6.29456 13.1506 6.48458 13.1639C6.67461 13.1773 6.86461 13.1376 7.03346 13.0495L9.00001 12.0255L10.9491 13.0727C11.0959 13.1508 11.2599 13.1908 11.4262 13.1891C11.6351 13.1852 11.8379 13.1183 12.008 12.9971C12.1619 12.886 12.2813 12.7336 12.3523 12.5576C12.4234 12.3816 12.4432 12.189 12.4095 12.0022L12.0371 9.82619L13.6546 8.26692C13.7915 8.1342 13.8883 7.96548 13.9336 7.7802C13.9788 7.59491 13.9709 7.4006 13.9106 7.21965C13.85 7.03724 13.7392 6.87558 13.5911 6.75314C13.4429 6.6307 13.2633 6.55243 13.0727 6.52729ZM13.0262 7.63856L11.2807 9.33747C11.2313 9.38837 11.1945 9.45015 11.1733 9.51785C11.1521 9.58555 11.147 9.65728 11.1586 9.72729L11.5658 12.1244C11.5722 12.1508 11.57 12.1786 11.5596 12.2037C11.5491 12.2288 11.5309 12.2499 11.5076 12.264C11.4861 12.281 11.4595 12.2902 11.432 12.2902C11.4046 12.2902 11.3779 12.281 11.3564 12.264L9.20365 11.1353C9.1412 11.1012 9.07117 11.0833 9.00001 11.0833C8.92885 11.0833 8.85882 11.1012 8.79637 11.1353L6.67274 12.2756C6.65118 12.2926 6.62454 12.3019 6.5971 12.3019C6.56966 12.3019 6.54301 12.2926 6.52146 12.2756C6.49819 12.2616 6.48 12.2405 6.46954 12.2153C6.45908 12.1902 6.45689 12.1624 6.46328 12.136L6.87055 9.73892C6.88208 9.66892 6.87703 9.59719 6.85581 9.52949C6.83459 9.46179 6.79779 9.40001 6.74837 9.3491L5.00292 7.65019C4.9728 7.63841 4.94825 7.61566 4.93422 7.58652C4.92019 7.55737 4.91771 7.524 4.92728 7.4931C4.93498 7.4671 4.95008 7.4439 4.97074 7.42633C4.99141 7.40877 5.01674 7.3976 5.04365 7.39419L7.45237 7.0451C7.52188 7.03453 7.58781 7.00732 7.64454 6.96579C7.70126 6.92425 7.74712 6.86963 7.77819 6.80656L8.86037 4.61892C8.93019 4.47929 9.04655 4.47929 9.11637 4.61892L10.1986 6.80656C10.2296 6.86963 10.2755 6.92425 10.3322 6.96579C10.3889 7.00732 10.4549 7.03453 10.5244 7.0451L12.9331 7.39419C12.96 7.3976 12.9853 7.40877 13.006 7.42633C13.0267 7.4439 13.0418 7.4671 13.0495 7.4931C13.061 7.51679 13.065 7.54348 13.0608 7.56951C13.0567 7.59555 13.0446 7.61966 13.0262 7.63856Z"
                      fill="#7B869B"
                    />
                    <path
                      d="M9 1C7.41775 1 5.87103 1.46919 4.55544 2.34824C3.23985 3.22729 2.21447 4.47672 1.60897 5.93853C1.00347 7.40034 0.84504 9.00887 1.15372 10.5607C1.4624 12.1126 2.22433 13.538 3.34315 14.6569C4.46197 15.7757 5.88743 16.5376 7.43928 16.8463C8.99113 17.155 10.5997 16.9965 12.0615 16.391C13.5233 15.7855 14.7727 14.7602 15.6518 13.4446C16.5308 12.129 17 10.5822 17 9C16.9969 6.87921 16.1531 4.84617 14.6535 3.34654C13.1538 1.84692 11.1208 1.00308 9 1ZM9 16.1273C7.59036 16.1273 6.21238 15.7093 5.0403 14.9261C3.86823 14.143 2.95471 13.0298 2.41526 11.7275C1.87582 10.4251 1.73467 8.99209 2.00968 7.60954C2.28469 6.22698 2.96349 4.95702 3.96026 3.96026C4.95703 2.96349 6.22699 2.28468 7.60954 2.00968C8.9921 1.73467 10.4252 1.87581 11.7275 2.41526C13.0298 2.9547 14.143 3.86822 14.9261 5.0403C15.7093 6.21237 16.1273 7.59036 16.1273 9C16.1242 10.8893 15.3723 12.7004 14.0363 14.0363C12.7004 15.3723 10.8893 16.1242 9 16.1273Z"
                      stroke="#7B869B"
                      strokeWidth="0.5"
                    />
                    <path
                      d="M13.0727 6.52729L10.8909 6.2131L9.91346 4.23492C9.82947 4.06433 9.69938 3.92068 9.53794 3.82022C9.3765 3.71976 9.19015 3.66651 9.00001 3.66651C8.80986 3.66651 8.62352 3.71976 8.46208 3.82022C8.30063 3.92068 8.17055 4.06433 8.08655 4.23492L7.1091 6.2131L4.92728 6.52729C4.73986 6.55547 4.56394 6.63511 4.4191 6.75735C4.27426 6.87959 4.16619 7.03963 4.10692 7.21965C4.04443 7.39901 4.03367 7.59234 4.07585 7.77753C4.11803 7.96272 4.21146 8.13231 4.34546 8.26692L5.92801 9.80292L5.55565 11.9789C5.52193 12.1657 5.54174 12.3583 5.61277 12.5343C5.68381 12.7104 5.80317 12.8627 5.9571 12.9738C6.11196 13.0848 6.29456 13.1506 6.48458 13.1639C6.67461 13.1773 6.86461 13.1376 7.03346 13.0495L9.00001 12.0255L10.9491 13.0727C11.0959 13.1508 11.2599 13.1908 11.4262 13.1891C11.6351 13.1852 11.8379 13.1183 12.008 12.9971C12.1619 12.886 12.2813 12.7336 12.3523 12.5576C12.4234 12.3816 12.4432 12.189 12.4095 12.0022L12.0371 9.82619L13.6546 8.26692C13.7915 8.1342 13.8883 7.96548 13.9336 7.7802C13.9788 7.59491 13.9709 7.4006 13.9106 7.21965C13.85 7.03724 13.7392 6.87558 13.5911 6.75314C13.4429 6.6307 13.2633 6.55243 13.0727 6.52729ZM13.0262 7.63856L11.2807 9.33747C11.2313 9.38837 11.1945 9.45015 11.1733 9.51785C11.1521 9.58555 11.147 9.65728 11.1586 9.72729L11.5658 12.1244C11.5722 12.1508 11.57 12.1786 11.5596 12.2037C11.5491 12.2288 11.5309 12.2499 11.5076 12.264C11.4861 12.281 11.4595 12.2902 11.432 12.2902C11.4046 12.2902 11.3779 12.281 11.3564 12.264L9.20365 11.1353C9.1412 11.1012 9.07117 11.0833 9.00001 11.0833C8.92885 11.0833 8.85882 11.1012 8.79637 11.1353L6.67274 12.2756C6.65118 12.2926 6.62454 12.3019 6.5971 12.3019C6.56966 12.3019 6.54301 12.2926 6.52146 12.2756C6.49819 12.2616 6.48 12.2405 6.46954 12.2153C6.45908 12.1902 6.45689 12.1624 6.46328 12.136L6.87055 9.73892C6.88208 9.66892 6.87703 9.59719 6.85581 9.52949C6.83459 9.46179 6.79779 9.40001 6.74837 9.3491L5.00292 7.65019C4.9728 7.63841 4.94825 7.61566 4.93422 7.58652C4.92019 7.55737 4.91771 7.524 4.92728 7.4931C4.93498 7.4671 4.95008 7.4439 4.97074 7.42633C4.99141 7.40877 5.01674 7.3976 5.04365 7.39419L7.45237 7.0451C7.52188 7.03453 7.58781 7.00732 7.64454 6.96579C7.70126 6.92425 7.74712 6.86963 7.77819 6.80656L8.86037 4.61892C8.93019 4.47929 9.04655 4.47929 9.11637 4.61892L10.1986 6.80656C10.2296 6.86963 10.2755 6.92425 10.3322 6.96579C10.3889 7.00732 10.4549 7.03453 10.5244 7.0451L12.9331 7.39419C12.96 7.3976 12.9853 7.40877 13.006 7.42633C13.0267 7.4439 13.0418 7.4671 13.0495 7.4931C13.061 7.51679 13.065 7.54348 13.0608 7.56951C13.0567 7.59555 13.0446 7.61966 13.0262 7.63856Z"
                      stroke="#7B869B"
                      strokeWidth="0.5"
                    />
                  </svg>
                </span>
                Priority Learning
              </p>
              <p className="priority-desc">
                Focus on high-impact topics first to make the most of your study
                time.
              </p>
            </div>
            <div>
              {priorityLearning?.map((item) => (
                <div className="priority-card" key={item.topic_id}>
                  <div className="priority-card-info-details">
                    <p
                      style={{
                        margin: "0",
                        fontSize: "14px",
                        color: "#49546C",
                      }}
                    >
                      {item.subject_name}
                    </p>
                    <p
                      style={{
                        margin: "0",
                        fontSize: "15px",
                        color: "#2A2A2A",
                      }}
                    >
                      {item.topic_name}
                    </p>
                    <p style={{ margin: "0", fontSize: "12px", color: "#888" }}>
                      Priority: {item.priority} | Est. Hours: {item.estd_hours}
                    </p>
                  </div>
                  <div className="priority-card-button">
                    {" "}
                    <button
                      className="priority-learn"
                      onClick={() => {
                        const message = `Short Notes on ${item.topic_name} from ${item.subject_name}`;
                        navigate(ROUTES.CHAT, {
                          state: {
                            toipic: "Learn Topic",
                            initialMessages: [
                              {
                                sender: "user",
                                message,
                                prompt: "Quick Topic Summary",
                              },
                            ],
                          },
                        });
                      }}
                    >
                      Learn This
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default InteractiveSyllabus;
